import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";
import { defaultSettings } from "@instead/shared";
import { api, auth } from "../lib/api.ts";
import { applyAppearance } from "../lib/theme.ts";
import { useT } from "../lib/i18n.ts";
import { Ic } from "../components/Icons.tsx";
import { FeedbackButton, FeedbackSheet } from "../components/Feedback.tsx";
import { TermsBanner } from "./Legal.tsx";

export function Shell() {
  const t = useT();
  const authed = !!auth.token();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: api.settings.get,
    enabled: authed,
    placeholderData: defaultSettings(),
  });

  useEffect(() => {
    applyAppearance(settings ?? defaultSettings());
  }, [settings]);

  const path = useRouterState({ select: (s) => s.location.pathname });
  // no app chrome before the account is actually in (a token alone isn't: the beta gate may still say no)
  // keyed by path: each screen fades in (--dur-2) instead of snapping into place
  const page = <div className="route" key={path}><Outlet /></div>;
  // the notice and the terms read like /signin: no chrome, whoever is looking (D-38)
  if (!authed || path.startsWith("/signin") || path.startsWith("/oauth/") || path.startsWith("/privacy") || path.startsWith("/terms")) return page;

  const restActive = ["/rest", "/deal", "/pick", "/set"].some((p) => path.startsWith(p));
  const shelfActive = ["/shelf", "/item", "/add"].some((p) => path.startsWith(p));
  const settingsActive = ["/settings", "/sources", "/connect"].some((p) => path.startsWith(p));
  const tab = (to: string, active: boolean, icon: React.ReactNode, label: string) => (
    <Link to={to} aria-current={active ? "page" : undefined}>
      <span className="glyph" aria-hidden="true">{icon}</span>
      {label}
    </Link>
  );

  return (
    <div className="shell">
      <main className="shell-main">
        <TermsBanner />
        {page}
      </main>
      <nav className="tabbar" aria-label="Main">
        {tab("/rest", restActive, Ic.rest({ size: 20 }), t("tab.rest"))}
        {tab("/shelf", shelfActive, Ic.shelf({ size: 20 }), t("tab.shelf"))}
        {tab("/settings", settingsActive, Ic.settings({ size: 20 }), t("tab.settings"))}
      </nav>
      {(settings?.features.feedbackButton ?? true) && <FeedbackButton />}
      <FeedbackSheet />
    </div>
  );
}
